import { useEffect, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useChatStore, type Message } from '../../store/chat.store';
import { StreamMessage } from './StreamMessage';

const SUGGESTED_PROMPTS = [
  'What purchase orders are open right now?',
  'Show me overdue invoices this month',
  'Summarise my vendor balance report',
  'Find items with negative inventory',
];

interface Props {
  messages?: Message[];
  suggestedPrompts?: string[];
  onPromptClick?: (prompt: string) => void;
  onRetryTurn?: (turnId: string) => void;
}

export function MessageStream({
  messages: messagesProp,
  suggestedPrompts = SUGGESTED_PROMPTS,
  onPromptClick,
  onRetryTurn,
}: Props) {
  const storeMessages = useChatStore((s) => s.messages);
  const messages = messagesProp ?? storeMessages;
  const scrollRef = useRef<HTMLDivElement>(null);

  const isAtBottomRef = useRef(true);

  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 120,
    overscan: 3,
  });

  // Track whether the user is near the bottom of the scroll container.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      isAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 80;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Always scroll when a new message is added (user sent or agent turn started).
  useEffect(() => {
    if (messages.length > 0) {
      virtualizer.scrollToIndex(messages.length - 1, { align: 'end' });
    }
  }, [messages.length]);

  // During streaming, follow new content only if the user hasn't scrolled up.
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last || last.status !== 'streaming' || !isAtBottomRef.current) return;
    virtualizer.scrollToIndex(messages.length - 1, { align: 'end' });
  }, [messages]);

  if (messages.length === 0) {
    return (
      <div className="flex flex-1 flex-col">
        <div className="flex flex-1 flex-col items-center justify-center gap-6 px-6 text-center">
          <div className="bg-ink/70 text-surface-elevated flex h-16 w-16 items-center justify-center rounded-full text-2xl font-semibold">
            A
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-neutral-900">
              How can I help?
            </h2>
            <p className="mx-auto max-w-xs text-sm text-neutral-500">
              Ask a question, look up a record, or pick a starting point.
              Suggestions are tailored to your role.
            </p>
          </div>
          {suggestedPrompts.length > 0 && (
            <div className="flex w-full max-w-xs flex-col gap-2">
              {suggestedPrompts.map((prompt) => (
                <button
                  key={prompt}
                  onClick={() => onPromptClick?.(prompt)}
                  className="w-full rounded-sm border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-100 active:bg-neutral-200"
                >
                  {prompt}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto">
      <div
        className="relative mx-auto my-8 w-full max-w-3xl px-4 py-4"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualizer.getVirtualItems().map((item) => {
          const msg = messages[item.index];
          return (
            <div
              key={item.key}
              data-index={item.index}
              ref={virtualizer.measureElement}
              className="absolute top-0 left-0 w-full pb-3"
              style={{ transform: `translateY(${item.start}px)` }}
            >
              <div
                className={msg.role === 'user' ? 'flex justify-end' : undefined}
              >
                <div
                  className={[
                    'rounded-sm px-4 py-3 text-sm leading-relaxed',
                    msg.role === 'user'
                      ? 'bg-ink/8 text-ink mr-2 max-w-[85%]'
                      : 'w-full text-neutral-900',
                  ].join(' ')}
                >
                  <StreamMessage
                    blocks={msg.content}
                    status={msg.status}
                    toolCallLabel={msg.toolCallLabel}
                    isLast={item.index === messages.length - 1}
                    onSuggestionClick={onPromptClick}
                    onRetry={
                      msg.status === 'disconnected' || msg.status === 'error'
                        ? () => onRetryTurn?.(msg.id)
                        : undefined
                    }
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
